import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContentEditor } from '@/components/ui/content-editor';
import { 
  Save, 
  Eye, 
  Send, 
  Plus,
  Trash2,
  Star,
  Globe,
  FileText,
  HelpCircle,
  List,
  Image,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { supabase, Service } from '@/lib/supabase';
import slugify from 'slugify';

interface ServiceEditorProps {
  service?: Service;
  onSave?: (service: Service) => void;
  onCancel?: () => void;
  onPreview?: (service: Service) => void;
}

interface ServiceFormData {
  title: string;
  slug: string;
  description: string;
  hero_headline?: string;
  content: string;
  status: 'draft' | 'published' | 'archived';
  is_featured: boolean;
  is_footer_service: boolean;
  meta_title?: string;
  meta_description?: string;
  hero_image?: string;
  service_areas: string[];
  benefits: Array<{ id: string; title: string; description: string }>;
  process_steps: Array<{ id: string; step_number: number; title: string; description: string }>;
  faqs: Array<{ id: string; question: string; answer: string }>;
  pricing_info?: string;
  cta_text?: string;
  cta_url?: string;
  category?: string;
  tags: string[];
}

const createService = async (data: Partial<Service>): Promise<Service> => {
  const { data: result, error } = await supabase
    .from('services')
    .insert([{
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

const updateService = async (id: string, data: Partial<Service>): Promise<Service> => {
  const { data: result, error } = await supabase
    .from('services')
    .update({
      ...data,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const ServiceEditor: React.FC<ServiceEditorProps> = ({
  service,
  onSave,
  onCancel,
  onPreview
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!service;

  const [formData, setFormData] = useState<ServiceFormData>({
    title: service?.title || '',
    slug: service?.slug || '',
    description: service?.description || '',
    hero_headline: service?.hero_headline || '',
    content: service?.content || '',
    status: service?.status || 'draft',
    is_featured: service?.is_featured || false,
    is_footer_service: service?.is_footer_service || false,
    meta_title: service?.meta_title || '',
    meta_description: service?.meta_description || '',
    hero_image: service?.hero_image || '',
    service_areas: service?.service_areas || [],
    benefits: service?.benefits || [],
    process_steps: service?.process_steps || [],
    faqs: service?.faqs || [],
    pricing_info: service?.pricing_info || '',
    cta_text: service?.cta_text || 'Get Quote',
    cta_url: service?.cta_url || '/contact',
    category: service?.category || '',
    tags: service?.tags || []
  });

  const [activeTab, setActiveTab] = useState('general');
  const [seoScore, setSeoScore] = useState(85);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !isEditing) {
      const newSlug = slugify(formData.title, { lower: true, strict: true });
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
  }, [formData.title, isEditing]);

  // Auto-generate meta title if not set
  useEffect(() => {
    if (formData.title && !formData.meta_title) {
      setFormData(prev => ({ ...prev, meta_title: `${formData.title} - FixMyBrick` }));
    }
  }, [formData.title, formData.meta_title]);

  // Calculate SEO score
  useEffect(() => {
    let score = 0;
    if (formData.title.length >= 30 && formData.title.length <= 60) score += 20;
    if (formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160) score += 20;
    if (formData.description && formData.description.length >= 100) score += 15;
    if (formData.content && formData.content.length >= 300) score += 15;
    if (formData.hero_image) score += 10;
    if (formData.benefits.length >= 3) score += 10;
    if (formData.process_steps.length >= 3) score += 10;
    setSeoScore(score);
  }, [formData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ServiceFormData) => {
      const serviceData = {
        ...data,
      };

      if (isEditing && service) {
        return updateService(service.id, serviceData);
      } else {
        return createService(serviceData);
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      if (onSave) onSave(result);
    },
    onError: (error) => {
      console.error('Error saving service:', error);
    }
  });

  const handleInputChange = (field: keyof ServiceFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async (status?: ServiceFormData['status']) => {
    const dataToSave = status ? { ...formData, status } : formData;
    saveMutation.mutate(dataToSave);
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview({
        ...formData,
        id: service?.id || 'preview',
        created_at: service?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as Service);
    }
  };

  // Benefits management
  const addBenefit = () => {
    const newBenefit = {
      id: Date.now().toString(),
      title: '',
      description: ''
    };
    setFormData(prev => ({
      ...prev,
      benefits: [...prev.benefits, newBenefit]
    }));
  };

  const updateBenefit = (id: string, field: 'title' | 'description', value: string) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.map(benefit =>
        benefit.id === id ? { ...benefit, [field]: value } : benefit
      )
    }));
  };

  const removeBenefit = (id: string) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter(benefit => benefit.id !== id)
    }));
  };

  // Process steps management
  const addProcessStep = () => {
    const newStep = {
      id: Date.now().toString(),
      step_number: formData.process_steps.length + 1,
      title: '',
      description: ''
    };
    setFormData(prev => ({
      ...prev,
      process_steps: [...prev.process_steps, newStep]
    }));
  };

  const updateProcessStep = (id: string, field: 'title' | 'description', value: string) => {
    setFormData(prev => ({
      ...prev,
      process_steps: prev.process_steps.map(step =>
        step.id === id ? { ...step, [field]: value } : step
      )
    }));
  };

  const removeProcessStep = (id: string) => {
    setFormData(prev => ({
      ...prev,
      process_steps: prev.process_steps.filter(step => step.id !== id)
        .map((step, index) => ({ ...step, step_number: index + 1 }))
    }));
  };

  // FAQ management
  const addFAQ = () => {
    const newFAQ = {
      id: Date.now().toString(),
      question: '',
      answer: ''
    };
    setFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, newFAQ]
    }));
  };

  const updateFAQ = (id: string, field: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.map(faq =>
        faq.id === id ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const removeFAQ = (id: string) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.filter(faq => faq.id !== id)
    }));
  };

  const getSeoScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeoScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (score >= 60) return <Clock className="h-4 w-4 text-yellow-600" />;
    return <AlertCircle className="h-4 w-4 text-red-600" />;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? 'Edit Service' : 'Create New Service'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update your service page content and settings.' : 'Create a comprehensive service page with SEO optimization.'}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleSave('draft')}
            disabled={saveMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button 
            onClick={() => handleSave('published')}
            disabled={saveMutation.isPending}
          >
            <Send className="mr-2 h-4 w-4" />
            Publish
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="benefits" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Benefits
              </TabsTrigger>
              <TabsTrigger value="process" className="flex items-center gap-2">
                <List className="h-4 w-4" />
                Process
              </TabsTrigger>
              <TabsTrigger value="faqs" className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                FAQs
              </TabsTrigger>
              <TabsTrigger value="gallery" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Gallery
              </TabsTrigger>
              <TabsTrigger value="seo" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                SEO
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Essential details about your masonry service.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title">Service Title *</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Brick Repair and Restoration"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.title.length}/60 characters
                    </p>
                  </div>

                  {/* Slug */}
                  <div className="space-y-2">
                    <Label htmlFor="slug">URL Slug *</Label>
                    <Input
                      id="slug"
                      placeholder="brick-repair-restoration"
                      value={formData.slug}
                      onChange={(e) => handleInputChange('slug', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      URL: /services/{formData.slug}
                    </p>
                  </div>

                  {/* Hero Headline */}
                  <div className="space-y-2">
                    <Label htmlFor="hero_headline">Hero Headline</Label>
                    <Input
                      id="hero_headline"
                      placeholder="Professional Brick Repair Services"
                      value={formData.hero_headline}
                      onChange={(e) => handleInputChange('hero_headline', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Appears prominently on the service page
                    </p>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Service Description *</Label>
                    <Textarea
                      id="description"
                      placeholder="Brief overview of your service for search results and previews..."
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.description.length}/160 characters (shown in search results)
                    </p>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <ContentEditor
                      label="Service Content *"
                      description="Detailed information about your service, process, and expertise."
                      value={formData.content}
                      onChange={(content) => handleInputChange('content', content)}
                      placeholder="Describe your service in detail..."
                      height={400}
                      showPreview={true}
                    />
                  </div>

                  {/* Service Features */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="is_featured"
                        checked={formData.is_featured}
                        onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                      />
                      <Label htmlFor="is_featured" className="flex items-center gap-2">
                        <Star className="h-4 w-4" />
                        Featured Service
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="is_footer_service"
                        checked={formData.is_footer_service}
                        onCheckedChange={(checked) => handleInputChange('is_footer_service', checked)}
                      />
                      <Label htmlFor="is_footer_service">Show in Footer</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Benefits Tab */}
            <TabsContent value="benefits" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Service Benefits</CardTitle>
                  <CardDescription>
                    Highlight the key benefits and advantages of this service.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.benefits.map((benefit, index) => (
                    <div key={benefit.id} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Benefit {index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBenefit(benefit.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Benefit Title</Label>
                        <Input
                          placeholder="e.g., Expert Craftsmanship"
                          value={benefit.title}
                          onChange={(e) => updateBenefit(benefit.id, 'title', e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          placeholder="Explain this benefit in detail..."
                          value={benefit.description}
                          onChange={(e) => updateBenefit(benefit.id, 'description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button onClick={addBenefit} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Benefit
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Process Tab */}
            <TabsContent value="process" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Service Process</CardTitle>
                  <CardDescription>
                    Outline the step-by-step process for this service.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.process_steps.map((step) => (
                    <div key={step.id} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Step {step.step_number}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeProcessStep(step.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Step Title</Label>
                        <Input
                          placeholder="e.g., Initial Assessment"
                          value={step.title}
                          onChange={(e) => updateProcessStep(step.id, 'title', e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Step Description</Label>
                        <Textarea
                          placeholder="Describe what happens in this step..."
                          value={step.description}
                          onChange={(e) => updateProcessStep(step.id, 'description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button onClick={addProcessStep} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Process Step
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* FAQs Tab */}
            <TabsContent value="faqs" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Frequently Asked Questions</CardTitle>
                  <CardDescription>
                    Address common questions about this service.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.faqs.map((faq, index) => (
                    <div key={faq.id} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">FAQ {index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFAQ(faq.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Question</Label>
                        <Input
                          placeholder="e.g., How long does brick repair take?"
                          value={faq.question}
                          onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Answer</Label>
                        <Textarea
                          placeholder="Provide a helpful answer..."
                          value={faq.answer}
                          onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button onClick={addFAQ} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add FAQ
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Service Gallery</CardTitle>
                  <CardDescription>
                    Manage images showcasing this service and past work.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <Image className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No images yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Gallery integration coming soon with asset library.
                    </p>
                    <div className="mt-6">
                      <Button variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Images
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SEO Tab */}
            <TabsContent value="seo" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>SEO Settings</CardTitle>
                  <CardDescription>
                    Optimize this service page for search engines.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Meta Title */}
                  <div className="space-y-2">
                    <Label htmlFor="meta_title">Meta Title</Label>
                    <Input
                      id="meta_title"
                      placeholder="SEO title for search engines..."
                      value={formData.meta_title}
                      onChange={(e) => handleInputChange('meta_title', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.meta_title?.length || 0}/60 characters
                    </p>
                  </div>

                  {/* Meta Description */}
                  <div className="space-y-2">
                    <Label htmlFor="meta_description">Meta Description</Label>
                    <Textarea
                      id="meta_description"
                      placeholder="Describe your service for search engine results..."
                      value={formData.meta_description}
                      onChange={(e) => handleInputChange('meta_description', e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.meta_description?.length || 0}/160 characters
                    </p>
                  </div>

                  {/* Hero Image */}
                  <div className="space-y-2">
                    <Label htmlFor="hero_image">Hero Image URL</Label>
                    <Input
                      id="hero_image"
                      placeholder="https://example.com/image.jpg"
                      value={formData.hero_image}
                      onChange={(e) => handleInputChange('hero_image', e.target.value)}
                    />
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <Label>Service Category</Label>
                    <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="repair">Repair Services</SelectItem>
                        <SelectItem value="restoration">Restoration</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="construction">New Construction</SelectItem>
                        <SelectItem value="specialty">Specialty Services</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publishing Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Publishing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>CTA Button Text</Label>
                <Input
                  value={formData.cta_text}
                  onChange={(e) => handleInputChange('cta_text', e.target.value)}
                  placeholder="Get Quote"
                />
              </div>

              <div className="space-y-2">
                <Label>CTA URL</Label>
                <Input
                  value={formData.cta_url}
                  onChange={(e) => handleInputChange('cta_url', e.target.value)}
                  placeholder="/contact"
                />
              </div>
            </CardContent>
          </Card>

          {/* SEO Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                {getSeoScoreIcon(seoScore)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Overall Score</span>
                    <span className={`font-bold ${getSeoScoreColor(seoScore)}`}>
                      {seoScore}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        seoScore >= 80 ? 'bg-green-500' : 
                        seoScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${seoScore}%` }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Title Length</span>
                  <span className={formData.title.length >= 30 && formData.title.length <= 60 ? 'text-green-600' : 'text-red-600'}>
                    {formData.title.length >= 30 && formData.title.length <= 60 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Meta Description</span>
                  <span className={formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160 ? 'text-green-600' : 'text-red-600'}>
                    {formData.meta_description && formData.meta_description.length >= 120 && formData.meta_description.length <= 160 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Content Length</span>
                  <span className={formData.content && formData.content.length >= 300 ? 'text-green-600' : 'text-red-600'}>
                    {formData.content && formData.content.length >= 300 ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Hero Image</span>
                  <span className={formData.hero_image ? 'text-green-600' : 'text-red-600'}>
                    {formData.hero_image ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Benefits (3+)</span>
                  <span className={formData.benefits.length >= 3 ? 'text-green-600' : 'text-red-600'}>
                    {formData.benefits.length >= 3 ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Benefits</span>
                <span className="font-medium">{formData.benefits.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Process Steps</span>
                <span className="font-medium">{formData.process_steps.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>FAQs</span>
                <span className="font-medium">{formData.faqs.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Content Words</span>
                <span className="font-medium">
                  {formData.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServiceEditor;